import {
	createContext,
	useContext,
	useReducer,
	type ReactNode,
	type Dispatch,
} from 'react'
import { type loader as rootLoader } from '#app/root.tsx'
import { type Theme } from '#app/utils/theme.server.ts'

// Define the shape of our global state
type User = Awaited<ReturnType<typeof rootLoader>>['data']['user']

interface GlobalState {
	user: User | null
	theme: Theme | 'system'
	locale: string
	sidebarOpen: boolean
}

// Define action types
type GlobalStateAction =
	| { type: 'SET_USER'; payload: User | null }
	| { type: 'SET_THEME'; payload: Theme | 'system' }
	| { type: 'SET_LOCALE'; payload: string }
	| { type: 'TOGGLE_SIDEBAR' }
	| { type: 'SET_SIDEBAR_OPEN'; payload: boolean }

// Create the reducer
function globalStateReducer(
	state: GlobalState,
	action: GlobalStateAction,
): GlobalState {
	switch (action.type) {
		case 'SET_USER':
			return { ...state, user: action.payload }
		case 'SET_THEME':
			return { ...state, theme: action.payload }
		case 'SET_LOCALE':
			return { ...state, locale: action.payload }
		case 'TOGGLE_SIDEBAR':
			return { ...state, sidebarOpen: !state.sidebarOpen }
		case 'SET_SIDEBAR_OPEN':
			return { ...state, sidebarOpen: action.payload }
		default:
			return state
	}
}

// Create contexts
const GlobalStateContext = createContext<GlobalState | undefined>(undefined)
const GlobalStateDispatchContext = createContext<
	Dispatch<GlobalStateAction> | undefined
>(undefined)

// Provider component
interface GlobalStateProviderProps {
	children: ReactNode
	initialState?: Partial<GlobalState>
}

export function GlobalStateProvider({
	children,
	initialState,
}: GlobalStateProviderProps) {
	const [state, dispatch] = useReducer(globalStateReducer, {
		user: null,
		theme: 'system',
		locale: 'en',
		sidebarOpen: true,
		...initialState,
	})

	return (
		<GlobalStateContext.Provider value={state}>
			<GlobalStateDispatchContext.Provider value={dispatch}>
				{children}
			</GlobalStateDispatchContext.Provider>
		</GlobalStateContext.Provider>
	)
}

// Custom hooks
export function useGlobalState() {
	const context = useContext(GlobalStateContext)
	if (context === undefined) {
		throw new Error('useGlobalState must be used within a GlobalStateProvider')
	}
	return context
}

export function useGlobalStateDispatch() {
	const context = useContext(GlobalStateDispatchContext)
	if (context === undefined) {
		throw new Error(
			'useGlobalStateDispatch must be used within a GlobalStateProvider',
		)
	}
	return context
}

// Convenience hooks for specific state slices
export function useGlobalUser() {
	const { user } = useGlobalState()
	return user
}

export function useGlobalTheme() {
	const { theme } = useGlobalState()
	const dispatch = useGlobalStateDispatch()
	
	const setTheme = (newTheme: Theme | 'system') => {
		dispatch({ type: 'SET_THEME', payload: newTheme })
	}
	
	return { theme, setTheme }
}

export function useGlobalLocale() {
	const { locale } = useGlobalState()
	const dispatch = useGlobalStateDispatch()
	
	const setLocale = (newLocale: string) => {
		dispatch({ type: 'SET_LOCALE', payload: newLocale })
	}
	
	return { locale, setLocale }
}

export function useGlobalSidebar() {
	const { sidebarOpen } = useGlobalState()
	const dispatch = useGlobalStateDispatch()
	
	const toggleSidebar = () => {
		dispatch({ type: 'TOGGLE_SIDEBAR' })
	}
	
	const setSidebarOpen = (open: boolean) => {
		dispatch({ type: 'SET_SIDEBAR_OPEN', payload: open })
	}
	
	return { sidebarOpen, toggleSidebar, setSidebarOpen }
}